import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { viewConfigApi } from '../../services/benchmarkApi';
import {
  applyConfigToColumns,
  parseViewConfig,
  toColumnVisibilityModel,
} from './benchmarkViewConfigUtils';
import BenchmarkColumnCustomizationDialog from './BenchmarkColumnCustomizationDialog';

/**
 * Load config_de_view default and apply to DataGrid columns (AsroTrack pattern).
 * Preserves renderCell/valueFormatter from latest baseColumns via ref.
 */
export function useBenchmarkGridView(category, baseColumns) {
  const baseRef = useRef(baseColumns);
  baseRef.current = baseColumns;

  const [configOverrides, setConfigOverrides] = useState(null); // parsed config or null = use default loaded
  const [defaultConfig, setDefaultConfig] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });

  const fieldKey = useMemo(
    () => (baseColumns || []).map((c) => c.field).join('|'),
    [baseColumns]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await viewConfigApi.getDefault(category);
        const parsed = parseViewConfig(res.data?.viewJsonDefault);
        if (cancelled) return;
        setDefaultConfig(parsed);
        if (!configOverrides) {
          setConfigOverrides(parsed);
        }
      } catch {
        if (!cancelled) setDefaultConfig(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const columns = useMemo(() => {
    const base = baseRef.current || [];
    if (!configOverrides) return base.map((c) => ({ ...c }));
    return applyConfigToColumns(configOverrides, base);
  }, [configOverrides, fieldKey]);

  const columnVisibilityModel = useMemo(() => toColumnVisibilityModel(columns), [columns]);

  const setColumnVisibilityModel = useCallback((model) => {
    setConfigOverrides((prev) => {
      const base = baseRef.current || [];
      const current = applyConfigToColumns(prev || defaultConfig || { grid: { columns: [] } }, base);
      const nextCols = current.map((c) => ({
        name: c.field,
        title: c.headerName,
        visible: model[c.field] !== false,
        width: String(c.width || 120),
      }));
      return {
        category,
        view_name: 'Custom',
        grid: { columns: nextCols },
      };
    });
  }, [category, defaultConfig]);

  const applyCustomization = useCallback(
    (overrides) => {
      setConfigOverrides({
        category,
        view_name: 'Custom',
        grid: {
          columns: overrides.map((o) => ({
            name: o.field,
            title: o.headerName,
            visible: !o.hide,
            width: String(o.width || 120),
          })),
        },
      });
    },
    [category]
  );

  const restoreDefault = useCallback(() => {
    setConfigOverrides(defaultConfig);
  }, [defaultConfig]);

  const saveView = useCallback(
    async (viewName, columnConfigJson) => {
      const userId =
        localStorage.getItem('userId') || localStorage.getItem('user_id') || null;
      await viewConfigApi.saveView({
        viewName: viewName || 'My view',
        category,
        scope: 'user',
        userId,
        columnConfigJson,
      });
    },
    [category]
  );

  const setQuickFilterValue = useCallback((value) => {
    const v = value || '';
    setQuickFilter(v);
    setFilterModel((prev) => ({
      ...prev,
      quickFilterValues: v.trim() ? v.trim().split(/\s+/) : [],
    }));
  }, []);

  const onFilterModelChange = useCallback((model) => {
    setFilterModel(model);
    const q = (model.quickFilterValues || []).join(' ');
    setQuickFilter(q);
  }, []);

  return {
    columns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    quickFilter,
    setQuickFilter: setQuickFilterValue,
    filterModel,
    onFilterModelChange,
    customizeOpen,
    setCustomizeOpen,
    openCustomize: () => setCustomizeOpen(true),
    applyCustomization,
    restoreDefault,
    saveView,
    CustomizeDialog: (
      <BenchmarkColumnCustomizationDialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        columns={columns}
        category={category}
        onApply={applyCustomization}
        onRestoreDefault={() => {
          restoreDefault();
          setCustomizeOpen(false);
        }}
        onSaveView={saveView}
      />
    ),
  };
}

export { BenchmarkColumnCustomizationDialog };
export { BenchmarkGridToolbar } from './BenchmarkGridToolbar';
